import { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Button, FormField } from '@noor/ui';
import { UNIT_CODES } from '../config/unit-codes';
import { IKPU_CODE_LENGTH, type ProductFormValues } from '../model/schema';

/** White bordered field pill — overrides the design system's filled default. */
const FIELD =
  'rounded-3xl border-input bg-card shadow-xs focus-visible:border-input-border-focus';

/** Occupy space only when a validation message actually renders. */
const MESSAGE = 'min-h-0 px-1 empty:hidden';

/** Figma spacing: 6px between a field's label and its control. */
const GROUP = 'gap-1.5';

/** Section heading inside the card («Тип выбора», «Варианты»). */
const SECTION_TITLE = 'text-base font-semibold tracking-tight text-foreground';

/**
 * The «Тип выбора» radio pair drawn as the design's two bordered pills, each
 * with its radio on the RIGHT (Figma 840-17585/840-17594).
 *
 * Restyled here via child selectors rather than upstream: `FormField.RadioGroup`
 * renders a plain left-aligned list, which is what every other radio group in
 * the app wants — this is the one screen that draws them as cards.
 */
const CHOICE_PILLS = [
  'flex gap-5',
  '[&>div]:w-[323px] [&>div]:shrink-0 [&>div]:flex-row-reverse [&>div]:justify-between',
  '[&>div]:rounded-3xl [&>div]:border [&>div]:border-input [&>div]:bg-card',
  '[&>div]:px-4 [&>div]:py-3.5 [&>div]:shadow-xs',
  '[&_label]:text-sm [&_label]:font-normal [&_label]:tracking-tight [&_label]:text-foreground',
].join(' ');

/** The three platform languages, in the order the design lists them. */
const LOCALES = [
  { suffix: 'Ru', tag: '(Ru)' },
  { suffix: 'Uz', tag: '(Uz)' },
  { suffix: 'En', tag: '(En)' },
] as const;

interface ModifierGroupCardProps {
  /** Index of this group in the form's `modifiers` array. */
  index: number;
}

/**
 * One modifier group's editor (Figma 840-17545 / 840-17987): the group name in
 * three languages, how many options a customer may pick, and the variant rows.
 *
 * The design's «Обязательно» toggle and «3шт» quantity box ARE rendered as of
 * API v1.00.227: the independent `min_select`/`max_select` pair is back on the
 * wire (the two-value `selection_type` enum that made them unrepresentable is
 * deleted), so required-ness and the multi-pick cap are real fields again.
 * The cap box shows only for `multi` — `single` IS a cap of one.
 *
 * Existing catalogue groups are inserted from `ModifierStep`; this card edits
 * the selected/new group itself.
 */
export function ModifierGroupCard({ index }: ModifierGroupCardProps) {
  const { t } = useTranslation('eats');
  const form = useFormContext<ProductFormValues>();
  const { control } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: `modifiers.${index}.options`,
  });

  const unitOptions = useMemo(
    () =>
      UNIT_CODES.map(({ code, labelKey }) => ({
        value: code,
        label: t(`menu.product.form.units.${labelKey}`),
      })),
    [t],
  );

  // `useWatch` is untyped off a bare context, so the value is narrowed here
  // rather than trusted; anything unexpected reads as the single-pick default.
  const choiceType: unknown = useWatch({
    control,
    name: `modifiers.${index}.choiceType`,
  });
  const isMultiple = choiceType === 'multi';

  return (
    <div className="flex flex-col gap-6 rounded-3xl bg-muted px-5 py-6">
      {/* Group name, one input per language. */}
      <div className="flex flex-col gap-5 sm:flex-row">
        {LOCALES.map(({ suffix, tag }) => (
          <FormField
            key={suffix}
            name={`modifiers.${index}.name${suffix}`}
            className={`flex-1 ${GROUP}`}
          >
            <FormField.Label className="flex items-center gap-px px-1 text-sm font-medium tracking-tight text-foreground">
              {t('menu.product.form.modifiers.groupNameLabel')}
              {/* Only Ru is required: uz/en may genuinely not be translated
               * yet, and the backend reads a blank as "leave it alone". */}
              {suffix === 'Ru' && <span className="text-primary">*</span>}
              <span className="font-normal text-muted-foreground">{tag}</span>
            </FormField.Label>
            {/* The prompt is in the language the FIELD collects, not the one
             * the panel is set to — the Uz input asks in Uzbek whatever the UI
             * language is, so three identically-worded boxes stop looking like
             * a repeat of the same field. Same rule as the description row's
             * `descriptionPlaceholder{Ru,Uz,En}`. */}
            <FormField.Input
              className={FIELD}
              placeholder={t(`menu.product.form.modifiers.groupNamePlaceholder${suffix}`)}
            />
            <FormField.Message className={MESSAGE} />
          </FormField>
        ))}
      </div>

      {/* Choice type, with its two companion controls (both writable again as
       * of API v1.00.227): the «Обязательно» switch (`min_select`) and, for
       * `multi`, the per-customer cap (`max_select`). The cap box is MOUNTED
       * only while `multi` is chosen — `single` is a cap of one by
       * definition, and an unmounted field cannot submit a stale value. */}
      <div className="flex flex-col gap-4">
        <span className={SECTION_TITLE}>
          {t('menu.product.form.modifiers.choiceTypeLabel')}
        </span>
        <div className="grid grid-cols-[323px_323px_minmax(0,1fr)] items-end gap-5">
          <FormField name={`modifiers.${index}.choiceType`} className="col-span-2">
            <FormField.RadioGroup
              className={CHOICE_PILLS}
              options={[
                {
                  label: t('menu.product.form.modifiers.choiceSingle'),
                  value: 'single',
                },
                {
                  label: t('menu.product.form.modifiers.choiceMultiple'),
                  value: 'multi',
                },
              ]}
            />
            <FormField.Message className={MESSAGE} />
          </FormField>

          {/* The switch row wears the same bordered pill as the radio cards. */}
          <div className="flex min-w-0 items-center gap-5">
            <FormField
              name={`modifiers.${index}.required`}
              className="flex h-12 shrink-0 items-center gap-2.5"
            >
              <FormField.Label className="text-sm font-medium tracking-tight text-foreground">
                {t('menu.product.form.modifiers.requiredLabel')}
              </FormField.Label>
              <FormField.Switch />
            </FormField>

            {isMultiple && (
              <FormField
                name={`modifiers.${index}.maxSelect`}
                className={`w-[112px] shrink-0 ${GROUP}`}
              >
                <FormField.Input
                  type="number"
                  inputMode="numeric"
                  min={2}
                  max={50}
                  className={FIELD}
                  placeholder={t('menu.product.form.modifiers.maxSelectPlaceholder')}
                  aria-label={t('menu.product.form.modifiers.maxSelectLabel')}
                />
                <FormField.Message className={MESSAGE} />
              </FormField>
            )}
          </div>
        </div>
      </div>

      {/* Variants: a name and its surcharge, one row each. */}
      <div className="flex flex-col gap-4">
        <span className={SECTION_TITLE}>
          {t('menu.product.form.modifiers.optionsLabel')}
        </span>

        <div className="flex flex-col gap-2.5">
          {fields.map((field, optionIndex) => (
            <div
              key={field.id}
              className="flex flex-col gap-2.5 rounded-3xl border border-input/70 bg-card p-3 shadow-xs sm:p-4"
            >
              {/* Numbered header: which variant this is, and its remove action.
               * The delete lives here — not as a fourth column beside the
               * fiscal fields — so price/IKPU/unit share one clean row. */}
              <div className="flex items-center gap-2 px-1">
                <span
                  aria-hidden
                  className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground tabular-nums"
                >
                  {optionIndex + 1}
                </span>
                <span className="text-sm font-medium tracking-tight text-foreground">
                  {t('menu.product.form.modifiers.optionsLabel')} · {optionIndex + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => remove(optionIndex)}
                  disabled={fields.length <= 1}
                  aria-label={t('menu.product.form.modifiers.removeOption')}
                  className="ml-auto size-8 shrink-0 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                >
                  <Trash2Icon className="size-4" aria-hidden />
                </Button>
              </div>

              {/* Names, one input per language, like the group's own name. */}
              <div className="grid gap-2.5 sm:grid-cols-3">
                {LOCALES.map(({ suffix, tag }) => (
                  <FormField
                    key={suffix}
                    name={`modifiers.${index}.options.${optionIndex}.name${suffix}`}
                    className={`min-w-0 ${GROUP}`}
                  >
                    <FormField.Label className="flex items-center gap-px px-1 text-xs font-medium text-muted-foreground">
                      {t('menu.product.form.modifiers.optionNameLabel')}
                      <span className="font-normal">{tag}</span>
                    </FormField.Label>
                    <FormField.Input
                      className={FIELD}
                      placeholder={t(
                        `menu.product.form.modifiers.optionNamePlaceholder${suffix}`,
                      )}
                    />
                    <FormField.Message className={MESSAGE} />
                  </FormField>
                ))}
              </div>

              <div aria-hidden className="h-px bg-muted" />

              {/* Surcharge, IKPU code, measurement unit and VAT — one row. */}
              <div className="grid gap-2.5 sm:grid-cols-[minmax(0,5fr)_minmax(0,6fr)_minmax(0,5fr)_minmax(0,4fr)] sm:items-start">
                <FormField
                  name={`modifiers.${index}.options.${optionIndex}.priceDelta`}
                  className={`min-w-0 ${GROUP}`}
                >
                  <FormField.Price
                    className={FIELD}
                    placeholder={t('menu.product.form.modifiers.optionPricePlaceholder')}
                    suffix={t('menu.product.form.currencySuffix')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                <FormField
                  name={`modifiers.${index}.options.${optionIndex}.ikpuCode`}
                  className={`min-w-0 ${GROUP}`}
                >
                  <FormField.Masked
                    mask={'#'.repeat(IKPU_CODE_LENGTH)}
                    className={FIELD}
                    autoComplete="off"
                    placeholder={t('menu.product.form.modifiers.optionIkpuPlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                <FormField
                  name={`modifiers.${index}.options.${optionIndex}.unitCode`}
                  className={`min-w-0 ${GROUP}`}
                >
                  <FormField.Select
                    className={FIELD}
                    options={unitOptions}
                    placeholder={t('menu.product.form.modifiers.optionUnitPlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>

                <FormField
                  name={`modifiers.${index}.options.${optionIndex}.vatPercent`}
                  className={`min-w-0 ${GROUP}`}
                >
                  <FormField.Number
                    className={FIELD}
                    min={0}
                    max={100}
                    decimals={2}
                    suffix="%"
                    placeholder={t('menu.product.form.modifiers.optionVatPlaceholder')}
                  />
                  <FormField.Message className={MESSAGE} />
                </FormField>
              </div>
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          // Blank fiscal classifiers — never the product's own (see
          // `buildEmptyGroup` in `modifier-step.tsx`): each option is its
          // own fiscal line item, and the schema requires both explicitly.
          onClick={() =>
            append({
              nameRu: '',
              nameUz: '',
              nameEn: '',
              priceDelta: 0,
              ikpuCode: '',
              unitCode: '',
            })
          }
          className="h-12 self-start rounded-full border-2 border-dashed border-input bg-transparent px-4 text-base font-semibold text-foreground hover:bg-muted/60"
        >
          <PlusIcon className="size-[18px]" aria-hidden />
          {t('menu.product.form.modifiers.addOption')}
        </Button>
      </div>
    </div>
  );
}
